import { ApolloError } from "@apollo/client";
import { Divider, Typography } from "@mui/material";
import Button from "@mui/material/Button";
import { Theme } from "@mui/material/styles";
import makeStyles from "@mui/styles/makeStyles";
import { Auth } from "aws-amplify";
import { ErrorBox, ErrorBoxDialog } from "components/error";
import ConfirmGridActionDialog from "components/grid/ConfirmGridActionDialog";
import DataGridView from "components/grid/DataGridView";
import DeleteUserAction from "components/users/DeleteUserAction";
import Permissions from "components/users/Permissions";
import RoleFilterMenu from "components/users/RoleFilterMenu";
import Roles from "components/users/Roles";
import { SolutionPermission } from "features/access-management";
import { UserName } from "features/solution-member";
import { TenantPermission } from "features/tenant";
import {
  ScopedRole__SolutionScope,
  ScopedRole__TenantScope
} from "model/schema/globalTypes";
import React, { useEffect } from "react";
import { getAllUserRoles } from "./getAllUserRoles";
import { filterUsersByRoles, isRoleFilterActive } from "./helpers/filterUsers";
import { useRoleFilter } from "./helpers/useRoleFilter";

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    display: "flex",
    flexDirection: "column",
    padding: theme.spacing(3),
    flex: 1,
    overflowY: "hidden"
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between"
  },
  left: { display: "flex", alignItems: "center" },
  actions: { display: "flex", alignItems: "center" },
  button: {
    color: "#FFFFFF",
    marginLeft: theme.spacing(1),
    backgroundColor: theme.palette.primary.dark,
    textTransform: "none",
    "&:hover": {
      backgroundColor: theme.palette.primary.dark
    }
  },
  membersText: { fontSize: "1.25rem" },
  divider: {
    color: "#E4E6EF",
    height: 20,
    width: 2,
    margin: theme.spacing(0, 2)
  },
  membersCount: {
    color: "#7E8299",
    fontWeight: 500
  },
  cell: {
    display: "flex",
    alignItems: "center",
    height: "100%"
  },
  overflow: { overflow: "visible !important" },
  centered: {
    ".dx-datagrid-content .dx-datagrid-table .dx-row > td&": {
      verticalAlign: "middle"
    }
  },
  roleHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    width: "100%"
  },
  gridWrapper: {
    flex: 1,
    minHeight: 0,
    overflow: "auto"
  }
}));

export interface User<T> {
  name: string;
  firstName: string | null;
  lastName: string | null;
  roles: T[];
  permissions?: (SolutionPermission | TenantPermission | null)[] | null;
}

export type RoleType = ScopedRole__SolutionScope | ScopedRole__TenantScope;

export interface UsersProps<T extends RoleType> {
  users: User<T>[];
  allRoles: T[];
  loadingRoles: boolean;
  onRoleUpdated: (userName: string, roles: T[]) => void;
  isLoading?: boolean;
  error?: ApolloError | ApolloError[];
  title?: string;
  onRefresh?: () => void;
  headerActions?: React.ReactNode;
  onRemoveUser?: (userName: string) => void;
  removeDialog?: React.ReactNode;
  solutionName?: string;
  removeUserError?: ApolloError | ApolloError[] | null;
  updateUserRoleError?: ApolloError | ApolloError[] | null;
  resetRemoveUserError: () => void;
  resetUpdateUserRoleError: () => void;
  loadUsers: () => void;
  lastAdminWarning?: string;
  adminRole?: T;
  adminCount?: number;
  disableDeleteUser?: boolean;
}

function Users<T extends RoleType>({
  users,
  allRoles,
  loadingRoles,
  onRoleUpdated,
  isLoading,
  error,
  title,
  onRefresh,
  headerActions,
  onRemoveUser,
  removeDialog,
  solutionName,
  removeUserError,
  updateUserRoleError,
  resetRemoveUserError,
  resetUpdateUserRoleError,
  loadUsers,
  lastAdminWarning,
  adminRole,
  adminCount,
  disableDeleteUser
}: UsersProps<T>) {
  const classNames = useStyles();
  const [openRemoveUserDialog, setOpenRemoveUserDialog] = React.useState(false);
  const [userName, setUserName] = React.useState("");
  const [myUserName, setMyUserName] = React.useState("");
  const [filterMenuOpen, setFilterMenuOpen] = React.useState(false);
  const [filterMenuPosition, setFilterMenuPosition] = React.useState<{
    top: number;
    left: number;
  } | null>(null);

  const handleCloseRemoveUserDialog = React.useCallback(() => {
    setOpenRemoveUserDialog(false);
  }, []);

  const existingRoles = React.useMemo(() => getAllUserRoles(users), [users]);
  const { selectedRoles, handleRoleFilterChange } =
    useRoleFilter<T>(existingRoles);

  const displayedUsers = React.useMemo(
    () => filterUsersByRoles(users, selectedRoles),
    [users, selectedRoles]
  );

  const isFilterActive = React.useMemo(
    () => isRoleFilterActive(selectedRoles, existingRoles),
    [selectedRoles, existingRoles]
  );

  useEffect(() => {
    async function fetchUser() {
      try {
        const user = await Auth.currentAuthenticatedUser();
        setMyUserName(user?.username || "");
      } catch {
        setMyUserName("");
      }
    }
    fetchUser();
  }, []);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const handleFilterIconClick = React.useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      e.stopPropagation();
      const rect = e.currentTarget.getBoundingClientRect();
      setFilterMenuPosition({
        top: rect.bottom,
        left: rect.right
      });
      setFilterMenuOpen(true);
    },
    []
  );

  const handleCloseFilterMenu = React.useCallback(() => {
    setFilterMenuOpen(false);
    setFilterMenuPosition(null);
  }, []);

  const mutationError = updateUserRoleError || removeUserError;

  return (
    <div className={classNames.root}>
      <div className={classNames.header}>
        <div className={classNames.left}>
          <h5 className={classNames.membersText}>{title}</h5>
          <Divider orientation="vertical" className={classNames.divider} />
          <Typography className={classNames.membersCount}>
            {users.length} Total
          </Typography>
        </div>
        <div className={classNames.actions}>
          {headerActions}
          {onRefresh && (
            <Button
              variant="contained"
              data-testid="refresh-button"
              className={classNames.button}
              onClick={onRefresh}
            >
              Refresh
            </Button>
          )}
        </div>
        {openRemoveUserDialog ? (
          <ConfirmGridActionDialog
            message={`You are about to remove ${userName}`}
            onConfirm={() => onRemoveUser && onRemoveUser(userName)}
            open={openRemoveUserDialog}
            onClose={handleCloseRemoveUserDialog}
          />
        ) : null}
        <ErrorBoxDialog
          apolloError={mutationError}
          onClose={() => {
            handleCloseRemoveUserDialog();
            if (removeUserError) resetRemoveUserError();
            if (updateUserRoleError) resetUpdateUserRoleError();
          }}
        />
      </div>
      <div className={classNames.gridWrapper}>
        <DataGridView<User<T>>
          dataSource={displayedUsers}
          repaintChangesOnly={false}
          isLoading={isLoading}
          showFilterRow={false}
          allowSelection={false}
          height={error ? 35 : "100%"}
          onCellPrepared={e => {
            if (e.column.dataField === "roles") {
              e.cellElement.classList.add(classNames.overflow);
            }
          }}
          columns={[
            {
              colDef: {
                dataField: "name",
                caption: "Name",
                sortOrder: "asc",
                width: "auto"
              },
              render: (p: { data: User<T> }) => <UserName data={p.data} />
            },
            {
              colDef: {
                dataField: "roles",
                caption: "Roles",
                cssClass: classNames.centered,
                allowFiltering: false,
                headerCellRender: () => (
                  <div className={classNames.roleHeader}>
                    <span>Roles</span>
                    <button
                      type="button"
                      aria-label="Filter roles"
                      style={{
                        cursor: "pointer",
                        marginLeft: 8,
                        color: isFilterActive
                          ? "#3aa9ae"
                          : "rgba(149, 149, 149, 0.5)",
                        display: "inline-block",
                        background: "none",
                        border: 0,
                        padding: 0
                      }}
                      onClick={handleFilterIconClick}
                    >
                      <span className="dx-icon-filter"></span>
                    </button>
                  </div>
                )
              },
              render: ({ data }: { data: User<T> }) => (
                <Roles
                  userName={data.name}
                  myUserName={myUserName}
                  roles={data.roles}
                  allRoles={allRoles}
                  loadingRoles={loadingRoles}
                  onRoleUpdated={(roles: T[]) =>
                    onRoleUpdated(data.name, roles)
                  }
                  lastAdminWarning={lastAdminWarning}
                  adminRole={adminRole}
                  adminCount={adminCount}
                />
              )
            },
            {
              colDef: {
                caption: "Actions",
                width: 130,
                allowFiltering: false,
                allowSorting: false
              },
              render: ({ data }: { data: User<T> }) => {
                const permissionsValue = data.permissions || [];
                return (
                  <div className={classNames.cell}>
                    <Permissions
                      firstName={data.firstName}
                      lastName={data.lastName}
                      permissions={permissionsValue}
                    />
                    {onRemoveUser && (
                      <DeleteUserAction
                        data={data}
                        handleUserDeleteRequest={(userName: string) => {
                          setUserName(userName);
                          setOpenRemoveUserDialog(true);
                        }}
                        solutionName={solutionName}
                        myUserName={myUserName}
                        disabled={disableDeleteUser}
                      />
                    )}
                  </div>
                );
              }
            }
          ]}
        />
      </div>
      <RoleFilterMenu
        open={filterMenuOpen}
        onClose={handleCloseFilterMenu}
        anchorPosition={filterMenuPosition || undefined}
        existingRoles={existingRoles}
        selectedRoles={selectedRoles}
        onRoleFilterChange={handleRoleFilterChange}
      />
      {removeDialog}
      {error ? <ErrorBox apolloError={error} /> : null}
    </div>
  );
}

export default Users;
